import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import {rehypeBenchlingTags} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags} from 'llm/markdown/LLMMarkdownRemark';
import {BenchlingTagConfigs, BenchlingTagProperty, parseTableData} from 'llm/markdown/LLMMarkdownUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip, {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {Table, Column, Cell} from 'ui-library/simple-table/SimpleTable';

interface LLMMarkdownDependencies {
  onCitationClick?: (toolId: string) => void;
}

// Factory function to create tag configs with dependencies
export const createBenchlingTagConfigs = (deps: LLMMarkdownDependencies): BenchlingTagConfigs => ({
  tagConfigs: [
    {
      markdownTag: 'bnch_id',
      htmlTagName: 'idchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^[a-z]+_[a-zA-Z0-9]{8,16}$/.test(content),
      component: ({idValue}: Record<string, unknown>) => {
        const id = idValue as string;
        if (id && isModelBenchChipSupported(id)) {
          return <BenchChip id={id} size={ChipSize.MINI} />;
        }
        return <span>{id}</span>;
      },
      preserveContentWhenSaving: true,
    },
    {
      markdownTag: 'bnch_citation',
      htmlTagName: 'citationchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => content.length > 0,
      component: ({idValue}: Record<string, unknown>) => {
        const citationId = idValue as string;
        const handleClick = (): void => {
          if (deps.onCitationClick) {
            deps.onCitationClick(citationId);
          }
        };

        return (
          <span className={css(styles.citationButtonContainer)}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              icon="open"
              onClick={handleClick}
              iconAltText="View citation details"
              data-testid={`citation-button-${citationId}`}
            />
          </span>
        );
      },
      preserveContentWhenSaving: false,
    },
    {
      markdownTag: null,
      htmlTagName: 'table',
      properties: [],
      validator: (content: string) => content.length > 0,
      component: ({children}: Record<string, unknown>) => {
        const tableData = parseTableData(children);
        if (!tableData) {
          return <table>{children}</table>;
        }

        const {headers, rows} = tableData;
        return (
          <Table numRows={rows.length}>
            {headers.map((header, index) => (
              <Column
                key={index}
                header={<Cell>{header}</Cell>}
                renderCell={(rowIndex: number) => <Cell>{rows[rowIndex] && rows[rowIndex][index]}</Cell>}
              />
            ))}
          </Table>
        );
      },
      preserveContentWhenSaving: true,
    },
  ],
});

// Export a default config for backwards compatibility with tests
export const BENCHLING_TAG_CONFIGS: BenchlingTagConfigs = createBenchlingTagConfigs({});

const SAFE_MARKDOWN_TAGS = [
  // NOTE: We purposefully don't include a, img, script, etc. to limit risk.
  // headers
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  // text
  'p',
  'span',
  // rule & break
  'hr',
  'br',
  // code
  'code',
  'pre',
  // lists
  'li',
  'ul',
  'ol',
  // italics
  'em',
  // bold
  'strong',
  'b',
  // quotes
  'blockquote',
  'q',
  // strikethrough
  's',
  'del',
  // tables
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  // custom elements - dynamically add from config
  ...BENCHLING_TAG_CONFIGS.tagConfigs.map((config) => config.htmlTagName),
];

// Creates a sanitize schema for the markdown tags
const createSanitizeSchema = ({
  tagConfigs,
}: BenchlingTagConfigs): {
  tagNames: Array<string>;
  attributes: Record<string, Array<string>>;
} => ({
  tagNames: SAFE_MARKDOWN_TAGS,
  attributes: {
    '*': [],
    ...Object.fromEntries(tagConfigs.map((config) => [config.htmlTagName, config.properties])),
  },
});

export interface LLMMarkdownProps {
  markdown: string;
  onCitationClick?: (toolId: string) => void;
}

export default function LLMMarkdownSection({markdown, onCitationClick}: LLMMarkdownProps): JSX.Element {
  const tagConfigs = createBenchlingTagConfigs({onCitationClick});
  const components = Object.fromEntries(
    tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.component])
  );
  const sanitizeSchema = createSanitizeSchema(tagConfigs);

  return (
    <section>
      <ReactMarkdown
        allowedElements={SAFE_MARKDOWN_TAGS}
        remarkPlugins={[remarkGfm, [remarkBenchlingTags, tagConfigs]]}
        rehypePlugins={[
          [rehypeBenchlingTags, tagConfigs],
          [rehypeSanitize, sanitizeSchema],
        ]}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </section>
  );
}

const styles = StyleSheet.create({
  citationButtonContainer: {
    display: 'inline',
    padding: '0 4px',
  },
});
